import React, {useState, useEffect, FormEvent, useCallback, useRef} from "react"
import {DEBOUNCE_TIME, MIN_NUMBER_CHAR_TO_SHOW_AUTOCOMPLETE, TYPING_STATE} from "../../config/constants"
import useDebounce from "../../utils/debounce"
import {removeDeptFromSessionStorage} from "../../utils/removeDeptFromSessionStorage"

export type UseSearchBoxProps = {
    searchTerm: string
    autoFocus: boolean
    onTyping: (chars: string, siteUrl: string) => void
    onAutocompleteLoading: () => void
    onAutocompleteTyping: ({chars, territory, cookie}: {chars: string; territory: string; cookie?: string}) => void
    onSubmit: (searchTerm: string, siteUrl: string) => void
    onDrawerOpening: () => void
    inputSearchRef?: HTMLInputElement
    siteUrl: string
    autocompleteTerritory: string
    cookie?: string
}

const shouldDisplayAutocompletePanel = (text: string) => text?.length >= MIN_NUMBER_CHAR_TO_SHOW_AUTOCOMPLETE
const handleEmptySpace = (val: string) => (!val?.trim() ? "" : val)
/**
 * @description Reusable hook for managing searchbox state and handling common actions (focus, change, clear, submit)
 * @param props: @type {UseSearchBoxProps}
 * @returns handles for managing the search box
 */
export const useSearchBox = ({
    searchTerm,
    onAutocompleteLoading,
    onAutocompleteTyping,
    onSubmit,
    onTyping,
    onDrawerOpening,
    autoFocus,
    inputSearchRef,
    siteUrl,
    cookie,
    autocompleteTerritory,
}: UseSearchBoxProps) => {
    const [searchState, setSearchState] = useState(TYPING_STATE.IDLE)
    const searchRef = useRef(inputSearchRef || null)

    const debounceValue = shouldDisplayAutocompletePanel(searchTerm) ? DEBOUNCE_TIME : 0
    const term = useDebounce<string>(searchTerm, debounceValue)

    useEffect(() => {
        if (searchState === TYPING_STATE.IDLE) return

        onAutocompleteLoading()
        onTyping(searchTerm, siteUrl)
        if (!shouldDisplayAutocompletePanel(term)) {
            setSearchState(TYPING_STATE.RECENT_SEARCH)
        } else if (term === searchTerm) {
            onAutocompleteTyping({chars: term, territory: autocompleteTerritory, cookie})
            setSearchState(TYPING_STATE.AUTO_COMPLETE)
        }
        onDrawerOpening()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [term, searchTerm, searchState, siteUrl, autocompleteTerritory, cookie])

    useEffect(() => {
        if (autoFocus) {
            // deprioritize the focus() so the animations run first
            setTimeout(() => searchRef.current?.focus(), 0)
        }
    }, [inputSearchRef, autoFocus])

    const handleFocus = useCallback(() => {
        onDrawerOpening()
        const typingState = shouldDisplayAutocompletePanel(searchTerm)
            ? TYPING_STATE.AUTO_COMPLETE
            : TYPING_STATE.RECENT_SEARCH

        setSearchState(typingState)
    }, [onDrawerOpening, searchTerm])

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => onTyping(handleEmptySpace(e.target.value), siteUrl),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            removeDeptFromSessionStorage()
            const cleanSearchTerm = searchTerm.toLowerCase().trim()
            const hasChars = cleanSearchTerm.length > 0
            if (hasChars) onSubmit(cleanSearchTerm, siteUrl)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchTerm, siteUrl],
    )

    const handleClear = useCallback(
        () => {
            setSearchState(TYPING_STATE.IDLE)
            onTyping("", siteUrl)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [siteUrl],
    )

    const setTextInputRef = (element: HTMLInputElement) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        searchRef.current = element
    }

    return {
        handleFocus,
        handleClear,
        handleChange,
        handleSubmit,
        setTextInputRef,
    }
}
